import React, { useState, useEffect } from 'react';
import EditIcon from '@material-ui/icons/Edit';
import {
  Modal,
  Typography,
  withStyles,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import {
  deleteChatGroup,
  updateChatGroup,
  getChatGroup,
} from '~/store/dispatch/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MButton from '~/components/PF2MButton';
import GroupsManagerModal from '../GroupsManagerModal';
import ActiveCell from '~/components/PF2MDataTable/Cells/ActiveCell';


const GroupsConfigTab = () => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [openGroupManagerModal, setOpenGroupManagerModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const chatGroups = useSelector(s => s.dispatch.chatGroups);
  const equipments = useSelector(s => s.manager.equipments);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(getChatGroup());
    };

    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, getChatGroup]);

  const renderHeader = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        backgroundColor: '#fff',
        paddingBlock: '1rem',
      }}
    >
      <Typography variant="h4" align="center" gutterBottom>
        {translate('common:GroupsConfiguration')}
      </Typography>
      <PF2MButton onClick={() => setOpenGroupManagerModal(true)}>
        {translate('common:AddGroup')}
      </PF2MButton>
    </div>
  );

  const normalizeChatGroups = () => chatGroups?.map(cg => ({
    ...cg,
    equipments_id: JSON.parse(cg?.equipments_id),
  }));

  const columns = [
    {
      title: translate('common:Edit'),
      field: 'edit',
      render: row => (
        <span style={{ cursor: 'pointer' }}>
          <EditIcon
            htmlColor="#F2AA00"
            fontSize="large"
            onClick={() => {
              setSelectedGroup(row);
              setOpenGroupManagerModal(true);
            }}
          />
        </span>
      ),
    },
    {
      title: translate('common:Active'),
      field: 'active',
      render: row => (
        <ActiveCell
          editing
          value={row.active}
          bulkEdit={(..._props) => {
            setSelectedGroup(row);
            setIsAlertOpen(true);
          }}
        />
      ),
    },
    {
      title: translate('common:MessageGroupName'),
      field: 'name',
    },
    {
      title: translate('common:Equipments'),
      field: 'equipments_id',
      editable: 'never',
      render: row => (
        <div style={{ maxWidth: '50vw' }}>
          <PF2MSearchSelectOutlined
            value={row.equipments_id}
            placeholder={translate('common:Select')}
            multiple
            hideSelectedOptions={false}
            isClearable={false}
            onChange={({ target }) => {
              const { tableData, ...values } = row;
              dispatch(updateChatGroup({
                ...values,
                equipments_id: JSON.stringify(target.value?.map(({ value }) => value) || []),
                update_status: 'U',
              }));
            }}
          >
            {equipments
              .filter(eq => row.equipments_id.includes(eq.id))
              .map(e => ({ value: e.id, label: e.name }))}
          </PF2MSearchSelectOutlined>
        </div>
      ),
    },
  ];

  return (
    <div>
      <hr style={{ margin: '0px' }} />
      {renderHeader()}
      <hr style={{ margin: '0px' }} />
      <PF2MDataTable
        options={{
          selection: false,
          hideCommit: true,
          hideReload: true,
          onInactivateAll: true,
          hideAdd: true,
          hideEditColumn: true,
          toolbar: false,
        }}
        columns={columns}
        data={normalizeChatGroups()}
      />
      <Modal
        open={openGroupManagerModal}
        onClose={() => {
          setSelectedGroup(null);
          setOpenGroupManagerModal(false);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div>
          <GroupsManagerModal
            handleClose={() => {
              setSelectedGroup(null);
              setOpenGroupManagerModal(false);
            }}
            selectedGroupMessage={selectedGroup}
          />
        </div>
      </Modal>

      <PF2MAlertDialog
        confirmText={translate('common:Yes')}
        description={
          selectedGroup?.active === 0
            ? `${translate('common:ConfirmActivateMessageGroup')}: "${selectedGroup?.name}"`
            : `${translate('common:ConfirmDeactivateMessageGroup')}: "${selectedGroup?.name}"`
        }
        open={isAlertOpen}
        onConfirm={() => {
          setIsAlertOpen(false);
          setSelectedGroup(null);
          const { tableData, equipments_id, ...groupData } = selectedGroup;
          if (selectedGroup?.active === 0) {
            dispatch(
              updateChatGroup({
                ...groupData,
                active: 1,
                update_status: 'U',
                equipments_id: JSON.stringify(equipments_id),
              }),
            );
          } else {
            dispatch(deleteChatGroup({ ...groupData, update_status: 'D' }));
          }
        }}
        onClose={() => {
          setIsAlertOpen(false);
          setSelectedGroup(null);
        }}
      />
    </div>
  );
};

export default withStyles(styles)(GroupsConfigTab);
